'use client'

import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Store, Bike, MapPin, ChevronRight, Check } from 'lucide-react'
import { isQuoteExpired, QUOTE_CHECKOUT_MARGIN_MS } from '@/lib/delivery/dispatchable'
import { etaMinutes } from '@/lib/delivery/formatDropoff'
import { waitText, withCourier, type WaitEstimate } from '@/lib/microsite/waitEstimate'
import { WhyFeeLink } from './WhyFeeLink'

/**
 * Barra "¿Cómo lo recibes?" — la decisión de recojo/domicilio ANTES de ordenar,
 * sin bloquear (spec 2026-09-03 §4.2). Vive sticky en la entrada de la carta.
 *
 * SOLO MUESTRA. No hace fetch, no cotiza, no calcula: el precio es
 * `feeAmountCents / 100` y nada más. El cambio de modo y la apertura del paso de
 * dirección los ejecuta el padre (`RestaurantMicrosite`), dueño del estado.
 *
 * Frescura: un solo reloj, `isQuoteExpired` con QUOTE_CHECKOUT_MARGIN_MS. `now`
 * es inyectable para los tests; en producción es Date.now() y solo se lee cuando
 * hay cotización (sin cotización no hay nada que pueda vencer).
 */
export interface FulfillmentBarProps {
  mode: 'pickup' | 'delivery'
  pickupWaitMinutes: number | null
  /** Tiempo de espera de la tienda (PR K). En vivo pinta el rango y, a domicilio,
   *  cocina + repartidor; fijo (o ausente) deja los textos de siempre. */
  waitEstimate?: WaitEstimate
  dropoffLine: string | null
  quote: { feeAmountCents: number; etaDropoffSeconds?: number; expiresAt: string } | null
  now?: number
  accentColor: string
  onChangeMode: (mode: 'pickup' | 'delivery') => void
  onEditAddress: () => void
  onWhyFee?: () => void
}

export function FulfillmentBar({
  mode,
  pickupWaitMinutes,
  waitEstimate,
  dropoffLine,
  quote,
  now,
  accentColor,
  onChangeMode,
  onEditAddress,
  onWhyFee,
}: FulfillmentBarProps) {
  const t = useTranslations('public.onlineOrder.checkout')

  // Tick de 60 s SOLO mientras hay cotización: la barra pasa sola a "confirmar
  // dirección" cuando vence, sin que el cliente toque nada. Sin cotización, sin timer.
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!quote) return
    const id = setInterval(() => setTick((n) => n + 1), 60_000)
    return () => clearInterval(id)
  }, [quote])

  // Publica su alto como --ms-fulfill-h: la barra de categorías se apila
  // debajo de ésta (spec 2026-10-19 §4). Sin esta barra la variable no existe
  // y las categorías caen a 0px extra.
  const barRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = barRef.current
    if (!el) return
    const root = document.documentElement
    const publish = () => root.style.setProperty('--ms-fulfill-h', `${Math.round(el.offsetHeight)}px`)
    publish()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null
    ro?.observe(el)
    return () => {
      ro?.disconnect()
      root.style.removeProperty('--ms-fulfill-h')
    }
  }, [])

  // eslint-disable-next-line react-hooks/purity
  const fresh = !!quote && !isQuoteExpired(quote, now ?? Date.now(), QUOTE_CHECKOUT_MARGIN_MS)
  const eta = etaMinutes(quote?.etaDropoffSeconds)
  const live = waitEstimate?.mode === 'live' ? waitEstimate : null
  const fee = quote ? `$${(quote.feeAmountCents / 100).toFixed(2)}` : null
  const isDelivery = mode === 'delivery'

  const options = [
    { key: 'pickup' as const, Icon: Store, label: t('modePickup') },
    { key: 'delivery' as const, Icon: Bike, label: t('modeDelivery') },
  ]

  return (
    <div
      ref={barRef}
      className="sticky z-30 py-2.5 border-b"
      style={{
        top: 'var(--ms-nav-h, 56px)',
        background: 'var(--ms-bg)',
        borderColor: 'var(--ms-border)',
      }}
      data-testid="fulfillment-bar"
    >
      {/* Misma geometría que <section id="menu"> (px-5 sm:px-8 max-w-6xl mx-auto):
          la barra es HERMANA de la sección, no hija, así que el padding va aquí
          adentro y no como margen negativo. */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col gap-2">
        {/* En xs (375) el rótulo y el control no caben en una línea: el control baja
            a su propia fila en vez de partir "Recojo yo" en dos renglones. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-[11px] font-bold uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--ms-text-muted)' }}>
            {t('fulfillmentBarTitle')}
          </span>
          <div className="inline-flex shrink-0 rounded-full p-[3px] gap-[3px]" style={{ background: 'var(--ms-surface-2, rgba(0,0,0,0.06))' }}>
            {options.map((opt) => {
              const on = mode === opt.key
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => onChangeMode(opt.key)}
                  aria-pressed={on}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors"
                  style={on ? { background: 'var(--ms-surface, #fff)', color: 'var(--ms-text)', boxShadow: '0 1px 2px rgba(0,0,0,0.12)' } : { color: 'var(--ms-text-muted)' }}
                >
                  <opt.Icon className="w-3.5 h-3.5" style={on ? { color: accentColor } : undefined} />
                  {opt.label}
                  {on && <Check className="w-3 h-3" style={{ color: accentColor }} strokeWidth={3} />}
                </button>
              )
            })}
          </div>
        </div>

        {/* Línea de detalle: qué decide de verdad — cuánto tarda y cuánto cuesta. */}
        {!isDelivery && typeof pickupWaitMinutes === 'number' && (
          <p className="text-xs" style={{ color: 'var(--ms-text-soft)' }}>
            {live ? waitText(live, 'pickupMeta', t) : t('modePickupMeta', { minutes: pickupWaitMinutes })}
          </p>
        )}

        {isDelivery && !dropoffLine && (
          <button
            type="button"
            onClick={onEditAddress}
            aria-label={t('fulfillmentBarEnterAddress')}
            className="flex items-center gap-2 rounded-xl px-3 py-2 text-left text-sm transition hover:opacity-90"
            style={{ background: 'var(--ms-surface-2, rgba(0,0,0,0.05))', color: 'var(--ms-text)' }}
          >
            <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />
            <span className="flex-1 font-semibold">{t('fulfillmentBarEnterAddress')}</span>
            <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--ms-text-muted)' }} />
          </button>
        )}

        {isDelivery && dropoffLine && (
          <div
            className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm"
            style={{ background: 'var(--ms-surface-2, rgba(0,0,0,0.05))', color: 'var(--ms-text)' }}
          >
            <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />
            {/* La dirección manda. ETA y precio van debajo en xs y al lado desde sm, en
                texto secundario: el precio se dice, no se grita (decisión 2026-09-03).
                El "¿Por qué?" es un botón HERMANO: nunca un botón dentro de otro. */}
            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
              <button
                type="button"
                onClick={onEditAddress}
                className="min-w-0 truncate text-left font-semibold transition hover:opacity-90"
              >
                {dropoffLine}
              </button>
              {fresh && (eta != null || fee) && (
                <span className="min-w-0 text-xs sm:whitespace-nowrap sm:flex-shrink-0" style={{ color: 'var(--ms-text-muted)' }}>
                  {eta != null && (live ? waitText(withCourier(live, eta), 'deliveryMeta', t) : t('modeDeliveryEta', { minutes: eta }))}
                  {eta != null && fee && ' · '}
                  {fee && t('fulfillmentBarDeliveryFee', { fee })}
                  {fee && onWhyFee && (
                    <span className="whitespace-nowrap">
                      {' · '}
                      <WhyFeeLink onClick={onWhyFee} style={{ color: accentColor }} />
                    </span>
                  )}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={onEditAddress}
              className="flex items-center gap-1 flex-shrink-0 text-xs font-bold transition hover:opacity-90"
              style={{ color: accentColor }}
            >
              {fresh ? t('fulfillmentBarChange') : t('fulfillmentBarConfirmAddress')}
              <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--ms-text-muted)' }} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
